'use client'

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from 'ui/select'
import { HTMLProps } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

export interface ISelectMenuItems {
    id: string
    name: string
    /** When true, renders as a muted "show all" option distinct from real data options */
    isAllOption?: boolean
}

const SelectMenu = ({
    items,
    label,
    name,
    makeAsNameValue,
    defaultValue,
    required,
    set,
    className,
    value,
    disabled,
    manipulateUrl = false,
    addAllOption = false
}: {
    items: ISelectMenuItems[]
    label?: string
    makeAsNameValue?: boolean
    defaultValue?: string
    name?: string
    required?: boolean
    value?: string | null
    set?: (value: string) => void
    className?: HTMLProps<HTMLElement>['className']
    manipulateUrl?: boolean
    disabled?: boolean
    addAllOption?: boolean
}) => {
    const router = useRouter()
    const searchParams = useSearchParams()
    
    // Determine the controlled value:
    // If value prop is provided (even if null/undefined), use it (controlled mode)
    // Otherwise, fall back to URL param or defaultValue (uncontrolled mode)
    const controlledValue = value !== undefined
        ? (value ?? '')
        : (searchParams.get(name || '') || defaultValue || '')

    const handleChange = (newValue: string) => {
        if (set) {
            set(newValue)
        } else if (name && manipulateUrl) {
            const params = new URLSearchParams(searchParams.toString())
            if (newValue) {
                params.set(name, newValue)
            } else {
                params.delete(name)
            }
            router.push(`?${params.toString()}`)
        }
    }

    // Only pass value to Select if it's a non-empty string that exists in items
    // Radix Select requires value to match an item's value
    const selectValue = controlledValue && controlledValue !== '' && items.some(item => 
        (makeAsNameValue ? item.name : item.id) === controlledValue
    ) ? controlledValue : undefined

    return (
        <Select
            onValueChange={handleChange}
            required={required || false}
            value={selectValue}
            name={name}
            disabled={disabled}
        >
            <SelectTrigger className={`${className} grid relative `}>
                <SelectValue placeholder={`${label ? label : name ? name : ''}`} />
            </SelectTrigger>
            <SelectContent className='glassmorphism overflow-y-auto max-h-[50dvh]'>
                <SelectGroup>
                    <SelectLabel>{(label ? label : name)?.toLocaleUpperCase()}</SelectLabel>
                    {addAllOption && <SelectItem value='all'>All {label}</SelectItem>}
                    {items.map((item) => (
                        <SelectItem
                            className={
                                item.isAllOption
                                    ? '!truncate !line-clamp-1 text-muted-foreground italic'
                                    : '!truncate !line-clamp-1'
                            }
                            key={item.id}
                            value={makeAsNameValue ? item.name : item.id}
                        >
                            {item.name}
                        </SelectItem>
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
    )
}

export default SelectMenu
